import {
  getSupabaseBrowserClient,
  hasSupabasePublicConfig,
} from "../../Supabase/SupabaseConfig";

import {
  buildCanonicalFoodFilters,
  buildCanonicalFoodSearchResults,
  getFallbackCatalogFoods,
  getCanonicalFoodSearchMatch,
  paginateCanonicalFoodResults,
} from "./foodSearchService";
import {
  DEFAULT_SUPABASE_FOOD_COLUMNS,
  DEFAULT_SUPABASE_FOOD_EXACT_SEARCH_COLUMNS,
  DEFAULT_SUPABASE_FOOD_LIMIT,
  DEFAULT_SUPABASE_FOOD_NORMALIZED_SEARCH_COLUMNS,
  DEFAULT_SUPABASE_FOOD_ORDER_COLUMN,
  DEFAULT_SUPABASE_FOOD_PREFIX_SEARCH_COLUMNS,
  DEFAULT_SUPABASE_FOOD_SCHEMA,
  DEFAULT_SUPABASE_FOOD_SEARCH_COLUMNS,
  DEFAULT_SUPABASE_FOOD_VIEW,
  DEFAULT_SUPABASE_FOOD_VIEW_COLUMNS,
  SUPABASE_FOOD_SEARCH_TIER_LIMITS,
  buildSupabaseFoodExactSearchClause,
  buildSupabaseFoodPrefixSearchClause,
  buildSupabaseFoodSearchClause,
  shouldRunFuzzyFoodSearch,
  normalizeFoodSearchCode,
  normalizeFoodSearchText,
  normalizeSupabaseFoodRecord,
  normalizeSupabaseFoodRecords,
} from "./supabaseFoodCatalogShared";

function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

const catalogFoodSearchCache = new Map();
export const MIN_BROAD_CATALOG_SEARCH_QUERY_LENGTH = 3;
const TYPO_SEARCH_LIMIT_WITH_STRONG_LITERALS = 4;
const TYPO_SEARCH_LIMIT_DEFAULT = 8;

function buildCatalogFoodSearchCacheKey(options = {}) {
  return JSON.stringify({
    query: normalizeFoodSearchText(options?.query ?? options?.searchText ?? ""),
    filters: buildCanonicalFoodFilters(options?.filters),
    sortField: options?.sortField ?? null,
    cursor: Math.max(0, Number(options?.cursor) || 0),
    pageSize: Math.max(0, Number(options?.pageSize ?? options?.limit) || 0),
    source: hasSupabasePublicConfig() ? "supabase" : "fallback",
  });
}

function buildCatalogSearchBaseQuery({
  client = getSupabaseBrowserClient(),
  schema = DEFAULT_SUPABASE_FOOD_SCHEMA,
  view = DEFAULT_SUPABASE_FOOD_VIEW,
  columns = DEFAULT_SUPABASE_FOOD_VIEW_COLUMNS,
  filters = buildCanonicalFoodFilters(),
} = {}) {
  let query = client.schema(schema).from(view).select(columns);

  if (filters?.type === "food" || filters?.type === "supplement") {
    query = query.eq("type", filters.type);
  }

  return query;
}

export function shouldRunBroadCatalogSearch(query = "") {
  return normalizeFoodSearchText(query).length >= MIN_BROAD_CATALOG_SEARCH_QUERY_LENGTH;
}

export function buildProbedPage(rows, { cursor = 0, pageSize = DEFAULT_SUPABASE_FOOD_LIMIT } = {}) {
  const safeCursor = Math.max(0, Number(cursor) || 0);
  const safePageSize = Math.max(0, Number(pageSize) || 0);
  const normalizedRows = Array.isArray(rows) ? rows : [];

  if (safePageSize === 0) {
    return {
      rows: normalizedRows,
      hasMore: false,
      nextCursor: null,
    };
  }

  const hasMore = normalizedRows.length > safePageSize;

  return {
    rows: hasMore ? normalizedRows.slice(0, safePageSize) : normalizedRows,
    hasMore,
    nextCursor: hasMore ? safeCursor + safePageSize : null,
  };
}

function buildBrowseOrderQuery(query, sortField) {
  if (!hasText(sortField)) {
    return query
      .order("search_name_norm", { ascending: true, nullsFirst: false })
      .order("id", { ascending: true });
  }

  return query
    .order(sortField, { ascending: false, nullsFirst: false })
    .order("search_name_norm", { ascending: true, nullsFirst: false })
    .order("id", { ascending: true });
}

async function executeQuery(query) {
  const { data, error, count } = await query;

  if (error) {
    throw new Error(`[fetchCatalogFoodSearchPage] ${error.message}`);
  }

  return {
    data: normalizeSupabaseFoodRecords(data),
    count: Number.isFinite(count) ? count : null,
  };
}

async function executeRpc(client, functionName, args) {
  const { data, error } = await client.rpc(functionName, args);

  if (error) {
    throw new Error(`[${functionName}] ${error.message}`);
  }

  return {
    data: normalizeSupabaseFoodRecords(data),
  };
}

async function fetchFallbackCatalogFoodsPage({
  query = "",
  filters = buildCanonicalFoodFilters(),
  sortField = null,
  cursor = 0,
  pageSize = DEFAULT_SUPABASE_FOOD_LIMIT,
} = {}) {
  const catalogFoods = buildCanonicalFoodSearchResults({
    coachFoods: [],
    catalogFoods: getFallbackCatalogFoods(),
    query,
    filters,
    sortField,
  });
  const page = paginateCanonicalFoodResults(catalogFoods, {
    cursor,
    pageSize,
  });

  return {
    ...page,
    source: "fallback",
  };
}

async function fetchSupabaseCatalogBrowsePage({
  filters = buildCanonicalFoodFilters(),
  sortField = null,
  cursor = 0,
  pageSize = DEFAULT_SUPABASE_FOOD_LIMIT,
  client = getSupabaseBrowserClient(),
} = {}) {
  const browseQuery = buildBrowseOrderQuery(
    buildCatalogSearchBaseQuery({
      client,
      filters,
    }).range(cursor, cursor + pageSize),
    sortField,
  );
  const { data } = await executeQuery(browseQuery);
  const page = buildProbedPage(data, {
    cursor,
    pageSize,
  });

  return {
    foods: page.rows,
    hasMore: page.hasMore,
    nextCursor: page.nextCursor,
    source: "supabase",
  };
}

async function fetchSupabaseCatalogSearchPage({
  query = "",
  filters = buildCanonicalFoodFilters(),
  sortField = null,
  cursor = 0,
  pageSize = DEFAULT_SUPABASE_FOOD_LIMIT,
  client = getSupabaseBrowserClient(),
} = {}) {
  const normalizedQuery = normalizeFoodSearchText(query);
  const codeQuery = normalizeFoodSearchCode(query);
  const shouldRunBroadSearch = shouldRunBroadCatalogSearch(normalizedQuery);
  const safeCursor = Math.max(0, Number(cursor) || 0);
  const fallbackQueryLimit = Math.max(pageSize * 2, pageSize);
  const exactCodePromise = codeQuery
    ? executeQuery(
        buildCatalogSearchBaseQuery({
          client,
          filters,
        })
          .eq("search_code_norm", codeQuery)
          .limit(SUPABASE_FOOD_SEARCH_TIER_LIMITS.exactCode),
      )
    : Promise.resolve({ data: [] });
  const exactTextClause = buildSupabaseFoodExactSearchClause(
    normalizedQuery,
    DEFAULT_SUPABASE_FOOD_EXACT_SEARCH_COLUMNS,
  );
  const exactTextPromise = exactTextClause
    ? executeQuery(
        buildCatalogSearchBaseQuery({
          client,
          filters,
        })
          .or(exactTextClause)
          .limit(SUPABASE_FOOD_SEARCH_TIER_LIMITS.exactText),
      )
    : Promise.resolve({ data: [] });
  const prefixClause = buildSupabaseFoodPrefixSearchClause(
    normalizedQuery,
    DEFAULT_SUPABASE_FOOD_PREFIX_SEARCH_COLUMNS,
  );
  const prefixPromise = prefixClause
    ? executeQuery(
        buildCatalogSearchBaseQuery({
          client,
          filters,
        })
          .or(prefixClause)
          .limit(SUPABASE_FOOD_SEARCH_TIER_LIMITS.prefix),
      )
    : Promise.resolve({ data: [] });
  const fullTextPromise = shouldRunBroadSearch
    ? executeQuery(
        buildCatalogSearchBaseQuery({
          client,
          filters,
        })
          .textSearch("search_text", normalizedQuery, {
            config: "simple",
            type: "websearch",
          })
          .limit(SUPABASE_FOOD_SEARCH_TIER_LIMITS.fullText),
      ).catch(() => ({ data: [] }))
    : Promise.resolve({ data: [] });
  const fallbackClause = buildSupabaseFoodSearchClause(
    normalizedQuery,
    DEFAULT_SUPABASE_FOOD_NORMALIZED_SEARCH_COLUMNS,
  );
  const fallbackPromise = shouldRunBroadSearch && fallbackClause
    ? executeQuery(
        buildCatalogSearchBaseQuery({
          client,
          filters,
        })
          .or(fallbackClause)
          .range(safeCursor, safeCursor + fallbackQueryLimit),
      )
    : Promise.resolve({ data: [] });

  const [
    exactCodeResults,
    exactTextResults,
    prefixResults,
    fullTextResults,
    fallbackResults,
  ] = await Promise.all([
    exactCodePromise,
    exactTextPromise,
    prefixPromise,
    fullTextPromise,
    fallbackPromise,
  ]);

  const foodsById = new Map();
  [
    ...exactCodeResults.data,
    ...exactTextResults.data,
    ...prefixResults.data,
    ...fullTextResults.data,
    ...buildProbedPage(fallbackResults.data, {
      cursor: safeCursor,
      pageSize: fallbackQueryLimit,
    }).rows,
  ].forEach((food) => {
    if (!food?.id || foodsById.has(food.id)) {
      return;
    }

    foodsById.set(food.id, food);
  });

  const literalStrongCount = Array.from(foodsById.values()).filter((food) => {
    const match = getCanonicalFoodSearchMatch(food, normalizedQuery, {
      allowFuzzy: false,
    });
    return match?.tier != null && match.tier <= 2;
  }).length;
  const shouldRunTypoSearch =
    shouldRunFuzzyFoodSearch(query) && literalStrongCount < 5;
  const typoResults = shouldRunTypoSearch
    ? await executeRpc(client, "catalog_foods_typo_search", {
        input_query: normalizedQuery,
        input_type:
          filters?.type === "food" || filters?.type === "supplement"
            ? filters.type
            : null,
        input_limit:
          literalStrongCount > 0
            ? TYPO_SEARCH_LIMIT_WITH_STRONG_LITERALS
            : TYPO_SEARCH_LIMIT_DEFAULT,
      }).catch(() => ({ data: [] }))
    : { data: [] };

  typoResults.data.forEach((food) => {
    if (!food?.id || foodsById.has(food.id)) {
      return;
    }

    foodsById.set(food.id, food);
  });

  const foods = buildCanonicalFoodSearchResults({
    coachFoods: [],
    catalogFoods: Array.from(foodsById.values()),
    query: normalizedQuery,
    filters,
    sortField,
  });
  const fallbackPage = buildProbedPage(fallbackResults.data, {
    cursor: safeCursor,
    pageSize: fallbackQueryLimit,
  });

  return {
    foods,
    hasMore: fallbackPage.hasMore,
    nextCursor: fallbackPage.nextCursor,
    source: "supabase",
  };
}

export function clearCatalogFoodSearchCache() {
  catalogFoodSearchCache.clear();
}

export async function fetchCatalogFoodSearchPage(options = {}) {
  const cacheKey = buildCatalogFoodSearchCacheKey(options);
  if (catalogFoodSearchCache.has(cacheKey)) {
    return catalogFoodSearchCache.get(cacheKey);
  }

  const request = (async () => {
    if (!hasSupabasePublicConfig()) {
      return fetchFallbackCatalogFoodsPage(options);
    }

    if (normalizeFoodSearchText(options?.query ?? options?.searchText ?? "")) {
      return fetchSupabaseCatalogSearchPage(options);
    }

    return fetchSupabaseCatalogBrowsePage(options);
  })().catch((error) => {
    catalogFoodSearchCache.delete(cacheKey);
    throw error;
  });

  catalogFoodSearchCache.set(cacheKey, request);
  return request;
}

export async function fetchSupabaseFoods(options = {}) {
  const {
    client = getSupabaseBrowserClient(),
    schema = DEFAULT_SUPABASE_FOOD_SCHEMA,
    view = DEFAULT_SUPABASE_FOOD_VIEW,
    columns = DEFAULT_SUPABASE_FOOD_COLUMNS,
    limit = DEFAULT_SUPABASE_FOOD_LIMIT,
    searchText = "",
    searchColumns = DEFAULT_SUPABASE_FOOD_SEARCH_COLUMNS,
    orderBy = DEFAULT_SUPABASE_FOOD_ORDER_COLUMN,
    ascending = true,
  } = options;

  let query = client.schema(schema).from(view).select(columns);
  const searchClause = buildSupabaseFoodSearchClause(searchText, searchColumns);

  if (searchClause) {
    query = query.or(searchClause);
  }

  if (hasText(orderBy)) {
    query = query.order(orderBy, { ascending });
  }

  if (Number.isFinite(limit) && limit > 0) {
    query = query.limit(limit);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(`[fetchSupabaseFoods] ${error.message}`);
  }

  return normalizeSupabaseFoodRecords(data);
}

export async function fetchSupabaseFoodById(foodId, options = {}) {
  const {
    client = getSupabaseBrowserClient(),
    schema = DEFAULT_SUPABASE_FOOD_SCHEMA,
    view = DEFAULT_SUPABASE_FOOD_VIEW,
    columns = DEFAULT_SUPABASE_FOOD_VIEW_COLUMNS,
    idColumn = "id",
  } = options;
  const normalizedFoodId = String(foodId || "").trim();

  if (!normalizedFoodId) {
    return null;
  }

  const { data, error } = await client
    .schema(schema)
    .from(view)
    .select(columns)
    .eq(idColumn, normalizedFoodId)
    .maybeSingle();

  if (error) {
    throw new Error(`[fetchSupabaseFoodById] ${error.message}`);
  }

  return normalizeSupabaseFoodRecord(data);
}
